import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { BaseChart } from '@components/ui-chart/base-chart';
import { IChartCallbackParams } from '@models';

import isObject from 'lodash-es/isObject';
import * as numbro from 'numbro';
import { takeUntil } from 'rxjs/operators';

import {
    PIE_CHART_OPTIONS,
    PIE_RADIUS,
} from './ui-pie-chart.constants';
import {
    IPieChartData,
    PieChartData,
} from './ui-pie-chart.types';

const NUMBRO_OPTIONS: numbro.Format = {
    average: true,
    mantissa: 1,
    trimMantissa: true,
};

@Component({
    template: '',
    selector: 'ui-pie-chart',
    styleUrls: ['../base-chart.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class UiPieChartComponent
    extends BaseChart<string>
    implements OnChanges, OnDestroy {

    @Input()
    public data?: PieChartData;

    @Input()
    public doughnutRadiusPercentage = 0;

    @Input()
    public tooltipFormatter?: (params: IChartCallbackParams) => string;

    constructor(
        injector: Injector,
        protected _viewContainerRef: ViewContainerRef,
    ) {
        super(injector);
        this._extendOptions(PIE_CHART_OPTIONS);
        this._colorProvider.changes$
            .pipe(takeUntil(this._destroyed$))
            .subscribe(() => {
                this._extendOptions({
                    title: {
                        textStyle: {
                            color: this._colorProvider.textColor,
                        },
                    },
                });
            });

        this.chartOptions.tooltip.formatter = this._getTooltipText;
        this._legendVisibilityChange$
            .pipe(takeUntil(this._destroyed$))
            .subscribe(() => {
                this._updateSum();
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);

        if (
            this.doughnutRadiusPercentage != null &&
            changes.doughnutRadiusPercentage
        ) {
            this.chartOptions.series![0].radius[0] = `${this.doughnutRadiusPercentage / 100 * parseInt(PIE_RADIUS, 10)}%`;
        }

        if (
            this.data &&
            (
                changes.data ||
                changes.legend ||
                changes.colors
            )
        ) {
            // TODO: extract this into a NumbroFormatterModule :)
            this.chartOptions.series![0].label.normal.formatter =
                (item: { value: number; }) => numbro(item.value).format({
                    ...NUMBRO_OPTIONS,
                    totalLength: 2,
                });
            this.chartOptions.series![0].data = [];
            this.chartOptions.legend.data = [];

            const sum = this.data.reduce((acc: number, current) => (
                typeof current === 'number' ?
                    acc + current :
                    acc + current.value
            ), 0) as number; // ng test throws lint error

            this.data.forEach((data, index) => {
                let value: number;
                if (typeof data === 'number') {
                    value = data;
                } else {
                    value = data.value;
                }

                const pieData: {
                    value: number;
                    name: string;
                    label?: any;
                    itemStyle?: any;
                } = {
                    value,
                    name: (this.legend || [])[index] || '',
                };

                if (!pieData.value) {
                    pieData.label = {
                        show: false,
                    };
                } else if (
                    pieData.value / sum < .2 &&
                    (pieData.value / sum < .15 && pieData.value > 9)
                ) {
                    pieData.label = Object.assign(pieData.label || {}, {
                        position: 'outside',
                    });
                }

                if (this.colors) {
                    pieData.itemStyle = {
                        color: this.colors[index] || null,
                    };
                }

                this.chartOptions.series![0].data.push(pieData);

                if (this.legend) {
                    this.chartOptions.legend.data.push({
                        name: this.legend[index] || '',
                        icon: 'circle',
                    });
                }
            });
        }

        if (
            this.data &&
            (
                changes.data ||
                changes.legend ||
                changes.doughnutRadiusPercentage
            )
        ) {
            if (+this.doughnutRadiusPercentage > 0) {
                this._updateSum();
            } else {
                this.chartOptions.title!.text = '';
            }
        }
    }

    public onSegmentClick = (params: IChartCallbackParams) => {
        const clickedData = this.data![params.dataIndex];

        if (isObject(clickedData)) {
            this.segmentClick.emit((clickedData as IPieChartData).key);
        }
    }

    protected _onResize() {
        if (
            this._chart &&
            this.chartOptions.title &&
            this.chartOptions.title.text
        ) {
            const smallerSide = Math.min(
                this._viewContainerRef.element.nativeElement.offsetWidth,
                this._viewContainerRef.element.nativeElement.offsetHeight,
            );
            const maxTextWidth = parseInt(PIE_RADIUS, 10) * this.doughnutRadiusPercentage / 10000 * smallerSide;
            const fontSize = maxTextWidth / Math.max(4, this.chartOptions.title.text.length); // Formula deduced empirically.

            this.chartOptions.title.textStyle.fontSize = fontSize;
            this.chartOptions.title.padding[0] = -fontSize / 2;

            this.update();
        }
    }

    private _getTooltipText = (params: IChartCallbackParams) => {
        if (this.tooltipFormatter) {
            return this.tooltipFormatter(params);
        }

        const tooltipName = (this.data![params.dataIndex] as any).tooltipName;
        const seriesName = tooltipName ? tooltipName : params.name;

        return `
            <div class="chart-tooltip-container">
                <div class="tooltip-title">
                    <span class="tooltip-marker">${params.marker}</span>
                    <strong>${params.value}</strong>
                </div>
                <div class="tooltip-subtitle">
                    ${seriesName}
                </div>
            </div>`;
    }

    private _updateSum() {
        let pieSum = 0;

        this.data!.forEach((data, index) => {
            if (
                !this.legend ||
                !this.legend[index]
            ) { return; }

            const legendName = this.legend[index];

            if (
                !this.legendItemsVisibility ||
                !this.legendItemsVisibility[legendName]
            ) { return; }

            const value = ((data as IPieChartData).value != null ?
                (data as IPieChartData).value :
                (data as number));

            pieSum += value;
        });

        this.chartOptions.title!.text = numbro(pieSum).format(NUMBRO_OPTIONS);
        this._onResize();
    }
}
